import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Search, RefreshCw, ShieldCheck, Filter } from 'lucide-react';

const PAGE = 50;

function StatusPill({ value }) {
  if (!value) return <span className="text-slate-400 text-xs">—</span>;
  const v = String(value).toLowerCase();
  let cls = 'bg-slate-100 text-slate-700';
  if (v === 'ok' || v === 'active' || v === 'sent' || v === 'approved' || v === 'created') {
    cls = 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
  } else if (v.includes('fail') || v === 'rate_limited' || v === 'revoked' || v === 'rejected' || v === 'bad_password') {
    cls = 'bg-red-50 text-red-700 ring-1 ring-red-200';
  } else if (v === 'superseded' || v === 'revision_required' || v === 'unknown_email' || v === 'no_account') {
    cls = 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
  }
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{value}</span>;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return iso;
  }
}

export default function SecurityFeed() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [filters, setFilters] = useState({ event: '', q: '', date_from: '', date_to: '' });

  const params = useMemo(() => {
    const p = { limit: PAGE, skip };
    if (filters.event && filters.event !== '__all__') p.event = filters.event;
    if (filters.q.trim()) p.q = filters.q.trim();
    if (filters.date_from) p.date_from = new Date(filters.date_from).toISOString();
    if (filters.date_to) p.date_to = new Date(filters.date_to).toISOString();
    return p;
  }, [filters, skip]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/admin/security/events', { params });
      setRows(data.rows);
      setTotal(data.total);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load audit feed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get('/admin/security/event-types')
      .then(({ data }) => setEventTypes(data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const onSearch = (e) => { e.preventDefault(); setSkip(0); load(); };

  const exportCsv = async () => {
    try {
      const exportParams = { ...params };
      delete exportParams.limit; delete exportParams.skip;
      const res = await api.get('/admin/security/events.csv', {
        params: exportParams, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `trj-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV export failed');
    }
  };

  const reset = () => {
    setFilters({ event: '', q: '', date_from: '', date_to: '' });
    setSkip(0);
  };

  return (
    <div data-testid="security-feed-page">
      <div className="bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-gold" />
          <div>
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Security &amp; Compliance</div>
            <h1 className="font-display font-semibold text-2xl text-navy mt-1">Audit Activity</h1>
          </div>
        </div>
        <Button onClick={exportCsv} className="btn-navy" data-testid="security-export-csv">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="p-8 space-y-5">
        {/* Filters */}
        <form onSubmit={onSearch} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            <Filter className="h-3.5 w-3.5" /> Filters
          </div>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9" placeholder="Email, IP, user_id, course, certificate…"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  data-testid="security-search-input"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Event Type</Label>
              <Select
                value={filters.event || '__all__'}
                onValueChange={(v) => { setFilters((f) => ({ ...f, event: v === '__all__' ? '' : v })); setSkip(0); }}
              >
                <SelectTrigger className="mt-1" data-testid="security-event-filter"><SelectValue placeholder="All events" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__all__">All events</SelectItem>
                  {eventTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">From</Label>
              <Input
                type="datetime-local" className="mt-1"
                value={filters.date_from}
                onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setSkip(0); }}
                data-testid="security-date-from"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">To</Label>
              <Input
                type="datetime-local" className="mt-1"
                value={filters.date_to}
                onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setSkip(0); }}
                data-testid="security-date-to"
              />
            </div>
            <div className="md:col-span-1 flex items-end gap-2">
              <Button type="submit" size="sm" className="btn-gold w-full" data-testid="security-apply">Apply</Button>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <button type="button" onClick={reset} className="text-navy hover:text-gold font-semibold underline-offset-2 hover:underline">
              Reset filters
            </button>
            <div>
              Showing <span className="font-semibold text-navy">{rows.length}</span>
              {' '}of <span className="font-semibold text-navy">{total.toLocaleString()}</span> events
            </div>
          </div>
        </form>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent activity</div>
            <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="security-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-white">
                <tr className="text-left">
                  {['When (UTC)', 'Event', 'User', 'Related', 'Status', 'Actor / IP'].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold border-b border-slate-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid="security-row">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">{formatDate(r.at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-navy">{r.label || r.event}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{r.event}</div>
                    </td>
                    <td className="px-4 py-3">
                      {r.user_email ? (
                        <>
                          <div className="text-navy font-medium">{r.user_email}</div>
                          {r.user_name && <div className="text-xs text-slate-500">{r.user_name}</div>}
                        </>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700 font-mono">
                      {r.certificate_number && <div>📜 {r.certificate_number}</div>}
                      {r.course_id && <div>🎓 {r.course_id}</div>}
                      {r.program_id && <div>📚 {r.program_id}</div>}
                      {r.submission_id && <div>📝 {r.submission_id} {r.kind ? `(${r.kind})` : ''}</div>}
                      {!r.certificate_number && !r.course_id && !r.program_id && !r.submission_id && (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusPill value={r.status} /></td>
                    <td className="px-4 py-3">
                      {r.actor && <div className="text-xs text-slate-700">{r.actor}</div>}
                      {r.ip && <div className="text-[11px] text-slate-400 font-mono">{r.ip}</div>}
                      {!r.actor && !r.ip && <span className="text-slate-400 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !busy && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No events match the current filters.</td></tr>
                )}
                {busy && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > PAGE && (
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
              <div>Page {Math.floor(skip / PAGE) + 1} of {Math.max(1, Math.ceil(total / PAGE))}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={skip === 0 || busy}
                  onClick={() => setSkip((s) => Math.max(0, s - PAGE))} data-testid="security-prev">
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={skip + PAGE >= total || busy}
                  onClick={() => setSkip((s) => s + PAGE)} data-testid="security-next">
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500 italic">
          Showing the most recent activity. Full history is retained in the database — use filters and CSV export to investigate older events.
        </p>
      </div>
    </div>
  );
}
